/**
 * A plan, as SVG markup.
 *
 * The same drawing PlanView makes, without the gestures: for a thumbnail of an option, for
 * the sign-off, for anywhere a kitchen has to be recognised at a glance. Pure -- a string in,
 * no DOM -- so it can be tested and reused wherever a picture is needed.
 */
import * as room from './room.js'
import { offsets } from './layout.js'

const r1 = (n) => Math.round(n * 10) / 10

/**
 * Appliances that are not the depth of the run they stand in.
 *
 * A family fridge is 700 deep against a 560 bench, so on a plan it projects into the room by
 * 140 -- which is exactly the thing a person walking past it notices and a designer checks.
 * Kept in step with appliance.js's SIZE by a test.
 */
const APPLIANCE_D = { fridge: 700 }

/** What to write on an appliance, since at plan size it is otherwise a grey rectangle. */
const LETTER = { fridge: 'F', dishwasher: 'DW', oven: 'O' }

/** How far a corner unit reaches along the wall after this run, or 0 if there is none. */
function cornerReturn(plan, run, lim) {
  const w = room.segment(plan, run.wallId)
  if (!w || room.isIsland(plan, run.wallId)) return 0
  const nextWall = room.next(plan.walls, w)
  if (!nextWall || Math.abs(room.corner(w, nextWall) - Math.PI / 2) > 0.09) return 0
  if (!plan.runs.some((r) => r.kind === 'base' && r.wallId === nextWall.id)) return 0
  return lim.cornerReturn ?? 0
}

/**
 * The six points of an L, going round: along the wall, out to the return, back to the front.
 *
 * In the wall's own frame -- `d` along it from `s`, `n` into the room -- so it works at any
 * angle, and the return reaches R from the wall rather than R from the cabinet.
 */
function lFootprint(wall, s, w, depth, R) {
  const d = room.dir(wall), n = room.inward(wall)
  const at = (u, v) => ({ x: wall.x1 + d.x * u + n.x * v, y: wall.y1 + d.y * u + n.y * v })
  return [at(s, 0), at(s + w, 0), at(s + w, R), at(s + w - depth, R), at(s + w - depth, depth), at(s, depth)]
}

export function planSvg(plan, maker, opts = {}) {
  const W = opts.width ?? 300
  const H = opts.height ?? 220
  const PAD = opts.pad ?? 18
  const lim = maker.limits
  const bb = room.bounds(plan.walls)
  const w = Math.max(bb.maxX - bb.minX, 1), h = Math.max(bb.maxY - bb.minY, 1)
  const s = Math.min((W - PAD * 2) / w, (H - PAD * 2) / h)
  const ox = (W - w * s) / 2 - bb.minX * s
  const oy = (H - h * s) / 2 - bb.minY * s
  const px = (p) => ({ x: r1(p.x * s + ox), y: r1(p.y * s + oy) })
  const poly = (pts) => 'M' + pts.map(px).map((p) => p.x + ' ' + p.y).join(' L') + ' Z'

  const parts = []
  parts.push(`<path d="${poly(room.vertices(plan.walls))}" fill="${opts.floor ?? '#F5F1EB'}"/>`)

  const tints = ['#93A08C', '#8E9B87', '#98A491', '#8A977F']
  for (const run of plan.runs.filter((r) => r.kind !== 'wall')) {
    const wall = room.segment(plan, run.wallId)
    if (!wall) continue
    const n = room.inward(wall)
    const at = offsets(run.items)
    run.items.forEach((item, i) => {
      if (item.gap) return
      const spec = maker.cabinets.find((c) => c.id === item.specId)
      // A fridge is deeper than the run it stands in and sticks out into the room, which is
      // the single most useful thing a plan can say about one. Drawn at its own depth.
      const d = APPLIANCE_D[spec?.role] ?? lim.baseDepth
      const a = room.at(wall, run.from + at[i]), b = room.at(wall, run.from + at[i] + item.w)
      // An L on the plan as it is in the room, and only where the cabinet's far edge
      // actually reaches the end of its run. The generator aims a corner unit at the corner
      // and `findSpot` will bump it along when that spot is taken, so "is it a corner unit"
      // and "is it in the corner" are two different questions.
      const R = spec?.role === 'corner' && run.kind === 'base'
        && run.from + at[i] + item.w >= run.to - 1
        ? cornerReturn(plan, run, lim) : 0
      const pts = R > d
        ? lFootprint(wall, run.from + at[i], item.w, d, R)
        : [a, b, { x: b.x + n.x * d, y: b.y + n.y * d }, { x: a.x + n.x * d, y: a.y + n.y * d }]
      const fill = item.filler ? '#D8D2C8' : spec?.kind === 'tall' ? '#7E8B74'
        : spec?.role === 'fridge' || spec?.role === 'dishwasher' ? '#B9BEC2' : tints[i % tints.length]
      parts.push(`<path d="${poly(pts)}" fill="${fill}" stroke="#FAF8F5" stroke-width="1"/>`)
      const c = px({ x: (a.x + b.x) / 2 + n.x * d * 0.55, y: (a.y + b.y) / 2 + n.y * d * 0.55 })
      const rr = r1(Math.min(item.w, d) * 0.3 * s)
      if (spec?.role === 'sink') parts.push(`<circle cx="${c.x}" cy="${c.y}" r="${rr}" fill="#B9BEC2"/>`)
      if (spec?.role === 'cooktop') {
        const dd = room.dir(wall)
        for (const [u, v] of [[-0.22, -0.22], [0.22, -0.22], [-0.22, 0.22], [0.22, 0.22]]) {
          const q = { x: r1(c.x + (dd.x * u * item.w + n.x * v * d) * s), y: r1(c.y + (dd.y * u * item.w + n.y * v * d) * s) }
          parts.push(`<circle cx="${q.x}" cy="${q.y}" r="${r1(rr * 0.4)}" fill="#2B2B2E"/>`)
        }
      }
      // A letter, because at plan size a fridge and a cupboard are the same grey rectangle.
      if (LETTER[spec?.role]) {
        const size = r1(Math.min(item.w, d) * 0.42 * s)
        parts.push(`<text x="${c.x}" y="${r1(c.y + size * 0.36)}" font-family="system-ui, sans-serif"`
          + ` font-size="${size}" font-weight="600" fill="#5A5F63" text-anchor="middle">${LETTER[spec.role]}</text>`)
      }
      if (spec?.drawers > 0 && !spec.role) {
        // a couple of lines across, so drawers read as drawers
        const dd = room.dir(wall)
        for (const f of [0.33, 0.66]) {
          const p1 = px({ x: a.x + dd.x * item.w * f, y: a.y + dd.y * item.w * f })
          const p2 = px({ x: a.x + dd.x * item.w * f + n.x * d, y: a.y + dd.y * item.w * f + n.y * d })
          parts.push(`<path d="M${p1.x} ${p1.y}L${p2.x} ${p2.y}" stroke="#FAF8F5" stroke-width="0.8" opacity=".7"/>`)
        }
      }
    })
  }

  /*
   * Walls, broken over their openings.
   *
   * A wall is drawn as the stretches BETWEEN its doors and windows rather than as one line,
   * which is how a floor plan shows an opening and why one is legible at a glance. The
   * opening itself then gets its own mark: a door a leaf and a swing, a window a double
   * line.
   */
  for (const wall of plan.walls) {
    const L = room.length(wall)
    const carries = plan.runs.some((r) => r.wallId === wall.id)
    const cuts = (plan.openings ?? []).filter((o) => o.wallId === wall.id)
      .map((o) => ({ ...o, from: Math.max(0, o.at), to: Math.min(L, o.at + o.width) }))
      .filter((o) => o.to > o.from)
      .sort((a, b) => a.from - b.from)

    let at = 0
    const stroke = (s, e) => {
      if (e - s < 1) return
      const a = px(room.at(wall, s)), b = px(room.at(wall, e))
      parts.push(`<path d="M${a.x} ${a.y}L${b.x} ${b.y}" stroke="#1C1917" stroke-width="${carries ? 2.5 : 1.5}" opacity="${carries ? .8 : .28}" stroke-linecap="square"/>`)
    }
    for (const o of cuts) { stroke(at, o.from); at = Math.max(at, o.to) }
    stroke(at, L)

    for (const o of cuts) {
      const a = px(room.at(wall, o.from)), b = px(room.at(wall, o.to))
      if (o.kind === 'window') {
        // Two thin lines across the gap: the glass, seen from above.
        const n = room.inward(wall)
        for (const off of [-30, 30]) {
          const p1 = px({ x: room.at(wall, o.from).x + n.x * off, y: room.at(wall, o.from).y + n.y * off })
          const p2 = px({ x: room.at(wall, o.to).x + n.x * off, y: room.at(wall, o.to).y + n.y * off })
          parts.push(`<path d="M${p1.x} ${p1.y}L${p2.x} ${p2.y}" stroke="#1C1917" stroke-width="1" opacity=".45"/>`)
        }
      } else {
        // The leaf, swung into the room, and the arc it sweeps.
        const n = room.inward(wall)
        const w = o.to - o.from
        const hinge = room.at(wall, o.from)
        const leafEnd = { x: hinge.x + n.x * w, y: hinge.y + n.y * w }
        const hp = px(hinge), lp = px(leafEnd)
        parts.push(`<path d="M${hp.x} ${hp.y}L${lp.x} ${lp.y}" stroke="#1C1917" stroke-width="1.6" opacity=".6"/>`)
        const r = r1(w * s)
        parts.push(`<path d="M${lp.x} ${lp.y}A${r} ${r} 0 0 1 ${b.x} ${b.y}" fill="none" stroke="#1C1917" stroke-width="0.8" opacity=".28" stroke-dasharray="4 3"/>`)
        parts.push(`<path d="M${a.x} ${a.y}L${b.x} ${b.y}" stroke="#F5F1EB" stroke-width="2" opacity=".9"/>`)
      }
    }
  }

  return `<svg viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" xmlns="http://www.w3.org/2000/svg">${parts.join('')}</svg>`
}
